import { Bounds, Vector } from '@ardoq/graph';
import {
  attachCanvasEvents,
  detachCanvasEvents,
} from './interactions/eventhandlers';
import { debounce, isEqual, noop, throttle } from 'lodash';
import { renderSceneGraph } from './visual/scenegraph';

import { placeAndRoute } from './placeandroute/placeandroute';
import { mergeGraph } from './mergeGraph';
import {
  AQBLOCKS_BACKGROUNDFILL,
  AQBLOCKS_FIT_PADDING,
  AQBLOCKS_FOCUS_STROKE,
  AQBLOCKS_HOVER_DELAY,
  AQBLOCKS_NODECHANNEL_MINIMUM_SIZE,
  AQBLOCKS_NODE_MINIMUMSIZE,
  AQBLOCKS_ROUTINGCHANNEL_MINIMUM_SIZE,
  AQBLOCKS_ROUTING_PAD,
  AQBLOCKS_ROUTING_SEP,
  AQBLOCKS_TOOLTIP_DELAY,
} from './consts';
import { Projection } from './projection';
import {
  BlocksGraph,
  BlocksNode,
  BlocksVisualState,
  ItemSet,
  AqBlocks,
  BlocksLayoutStyle,
  OnContextMenu,
  OnTooltip,
  BlocksPlacementStyle,
  BlocksLayoutType,
  ExportRenderOptions,
  BlocksHoveredEvent,
  BlocksSelectedEvent,
  BlocksActivatedEvent,
  BlocksEditedEvent,
  BlocksFindRequestedEvent,
} from './types';
import { createItemSet } from './itemSet';

import { renderScrollbars } from './visual/scrollbars';
import { renderDebugGrid, renderGrid } from './visual/grid';
import {
  rectBloat,
  rectCenter,
  rectContains,
  rectHeight,
  rectWidth,
} from '../misc/geometry';
import { isGroup, isNodeHitVisible, isNodeOpen } from './util';
import { hitTestReference } from './visual/reference';
import { fixupLayout, resetLayout } from './layout/fixup';
import { DragHandler } from './interactions/types';
import {
  flipGroupsHorizontal,
  flipGroupsVertical,
  rotateGroups,
  setGroupsExpanded,
  setGroupsExpandedAll,
} from './layout/actions';

export type ObservedPropertyName =
  | 'canvas'
  | 'pointerCapture'
  | 'isFocused'
  | 'prefersReducedMotion'
  | 'graph'
  | 'layoutStyle'
  | 'placementStyle'
  | 'isReadonly'
  | 'isDisabled'
  | 'isUserSelectable'
  | 'onContextMenu'
  | 'onTooltip';

export class AqBlocksComponent extends HTMLElement implements AqBlocks {
  // #region generic visualisation actions
  public activate(items: ItemSet) {
    if (this.graph) {
      this.dispatchEvent(
        new BlocksActivatedEvent({ graph: this.graph, items: items })
      );
    }
  }

  public expandAll() {
    this.setExpandedAll(true);
  }
  public collapseAll() {
    this.setExpandedAll(false);
  }
  private setExpandedAll(value: boolean) {
    if (this.graph && setGroupsExpandedAll(this.graph, value)) {
      this.cancel();
      this.updateHovered.cancel();

      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );

      this.updateHovered();
      this.pendRender();
      this.dispatchEvent(new BlocksEditedEvent({ graph: this.graph }));
    }
  }

  public expand(groups?: BlocksNode[]) {
    this.setExpanded(groups ?? this.actionGroups(), true);
  }
  public collapse(groups?: BlocksNode[]) {
    this.setExpanded(groups ?? this.actionGroups(), false);
  }
  public toggleExpanded(groups?: BlocksNode[]) {
    const g = groups ?? this.actionGroups();
    const v = g && g.length > 0 && !isNodeOpen(g[0]);

    this.setExpanded(g, v);
  }
  private setExpanded(groups: BlocksNode[], value: boolean) {
    if (this.graph && groups.length > 0 && setGroupsExpanded(groups, value)) {
      this.cancel();
      this.updateHovered.cancel();

      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );

      this.updateHovered();
      this.pendRender();
      this.dispatchEvent(new BlocksEditedEvent({ graph: this.graph }));
    }
  }

  public zoomFit() {
    if (this.graph) {
      const snap = 1;
      const bounds: Bounds = [
        snap * Math.floor(this.graph.root.bounds[0] / snap),
        snap * Math.floor(this.graph.root.bounds[1] / snap),
        snap * Math.ceil(this.graph.root.bounds[2] / snap),
        snap * Math.ceil(this.graph.root.bounds[3] / snap),
      ];

      this.adjustToRect(bounds, AQBLOCKS_FIT_PADDING);
    }
  }
  public zoomIn() {
    this.zoomBy(1.1);
  }
  public zoomOut() {
    this.zoomBy(0.9);
  }
  public zoomBy(scale: number, center?: Vector) {
    const zoomCenter = center ?? rectCenter(this.projection.window);
    const zoomScale = this.projection.scale * scale;

    if (
      zoomScale > 0 &&
      isFinite(zoomScale) &&
      isFinite(zoomCenter[0]) &&
      isFinite(zoomCenter[1])
    ) {
      this.updateHovered.cancel();
      this.projection.zoomToPoint(zoomCenter, zoomScale);
      this.pointer = this.projection.unprojectPoint(this.pointerViewport);
      this.updateHovered();
      this.pendRender();
    }
  }

  public panLeft() {
    this.panBy([-0.1, 0]);
  }
  public panUp() {
    this.panBy([0, -0.1]);
  }
  public panRight() {
    this.panBy([0.1, 0]);
  }
  public panDown() {
    this.panBy([0, 0.1]);
  }
  private panBy(speed: Vector) {
    const window = this.projection.window;
    const offset = [
      speed[0] * rectWidth(window),
      speed[1] * rectHeight(window),
    ];
    const bounds: Bounds = [
      window[0] + offset[0],
      window[1] + offset[1],
      window[2] + offset[0],
      window[3] + offset[1],
    ];

    this.updateHovered.cancel();
    this.projection.adjustToBounds(bounds, 0);
    this.pointer = this.projection.unprojectPoint(this.pointerViewport);
    this.updateHovered();
    this.pendRender();
  }

  public flipHorizontal() {
    if (this.graph) {
      flipGroupsHorizontal(this.graph, this.actionGroups());
      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );
      this.pendRender();
    }
  }
  public flipVertical() {
    if (this.graph) {
      flipGroupsVertical(this.graph, this.actionGroups());
      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );
      this.pendRender();
    }
  }
  public rotate() {
    if (this.graph) {
      rotateGroups(this.graph, this.actionGroups());
      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );
      this.pendRender();
    }
  }
  public actionGroups() {
    const ret = [...this.selected.nodes()].filter(node => isGroup(node));
    return ret ?? [];
  }

  public adjustToRect(bounds: Bounds, margin: number = 0) {
    if (rectWidth(bounds) > 0 && rectHeight(bounds) > 0 && margin > 0) {
      this.updateHovered.cancel();
      this.projection.adjustToBounds(bounds, margin);
      this.pointer = this.projection.unprojectPoint(this.pointerViewport);
      this.updateHovered();
      this.pendRender();
    }
  }

  public panTo(wd: Vector, vp: Vector) {
    if (
      isFinite(wd[0]) &&
      isFinite(wd[1]) &&
      isFinite(vp[0]) &&
      isFinite(vp[1])
    ) {
      this.updateHovered.cancel();
      this.projection.panTo(wd, vp);
      this.pointer = this.projection.unprojectPoint(this.pointerViewport);
      this.updateHovered();
      this.pendRender();
    }
  }
  // #endregion

  // #region user properties
  public get prefersReducedMotion(): boolean {
    return this._prefersReducedMotion;
  }
  public set prefersReducedMotion(value: boolean) {
    this.propertyChanged(
      'prefersReducedMotion',
      this._prefersReducedMotion,
      (this._prefersReducedMotion = value)
    );
  }
  private _prefersReducedMotion: boolean = true;

  public get isUserSelectable() {
    return this._isUserSelectable;
  }
  public set isUserSelectable(value: boolean) {
    this.propertyChanged(
      'isUserSelectable',
      this._isUserSelectable,
      (this._isUserSelectable = value)
    );
  }
  private _isUserSelectable: boolean = true;

  public get isReadonly() {
    return this._isReadonly;
  }
  public set isReadonly(value: boolean) {
    this.propertyChanged(
      'isReadonly',
      this._isReadonly,
      (this._isReadonly = value)
    );
  }
  private _isReadonly: boolean = false;

  public get isDisabled() {
    return this._isDisabled;
  }
  public set isDisabled(value: boolean) {
    this.propertyChanged(
      'isDisabled',
      this._isDisabled,
      (this._isDisabled = value)
    );
  }
  private _isDisabled: boolean = false;

  public get graph() {
    return this._graph;
  }
  public set graph(value: BlocksGraph | null) {
    this.propertyChanged('graph', this._graph, (this._graph = value));
  }
  private _graph: BlocksGraph | null = null;

  public get layoutStyle() {
    return this._layoutStyle;
  }
  public set layoutStyle(value: BlocksLayoutStyle) {
    this.propertyChanged(
      'layoutStyle',
      this._layoutStyle,
      (this._layoutStyle = value)
    );
  }
  private _layoutStyle: BlocksLayoutStyle = {
    type: 'process',
    orientation: 'left-to-right',
    linksAggregation: 'byReferenceType',
  };

  public get placementStyle() {
    return this._placementStyle;
  }
  public set placementStyle(value: BlocksPlacementStyle) {
    this.propertyChanged(
      'placementStyle',
      this._placementStyle,
      (this._placementStyle = value)
    );
  }
  private _placementStyle: BlocksPlacementStyle = {
    nodeMinimumSize: AQBLOCKS_NODE_MINIMUMSIZE,
    nodeChannelMinimumSize: AQBLOCKS_NODECHANNEL_MINIMUM_SIZE,
    routingChannelMinimumSize: AQBLOCKS_ROUTINGCHANNEL_MINIMUM_SIZE,
    routingPad: AQBLOCKS_ROUTING_PAD,
    routingSep: AQBLOCKS_ROUTING_SEP,
  };
  // #endregion

  public setGroupLayoutType = (
    groups: BlocksNode[],
    layoutType: BlocksLayoutType
  ) => {
    let isUpdated = false;

    for (const group of groups) {
      if (group.children && group.layoutType !== layoutType) {
        isUpdated = true;
        group.isLayoutDirty = true;

        group.layoutType = layoutType;

        for (const child of group.children) {
          child.layoutData = undefined;
        }
      }
    }

    if (this.graph && isUpdated) {
      fixupLayout(this.graph, this.layoutStyle);
      placeAndRoute(
        this.graph,
        this.layoutStyle.linksAggregation,
        this.placementStyle
      );
      this.pendRender();
    }
  };

  // #region context menu handling
  public get doContextMenu() {
    return this._onContextMenu;
  }
  public set doContextMenu(value: OnContextMenu) {
    this.propertyChanged(
      'onContextMenu',
      this._onContextMenu,
      (this._onContextMenu = value)
    );
  }
  private _onContextMenu: OnContextMenu;
  private _cancelContextMenu = noop;
  public showContextMenu(items: ItemSet, client: Vector) {
    this._cancelContextMenu();
    this._cancelContextMenu = noop;

    if (this.doContextMenu) {
      this._cancelContextMenu = this.doContextMenu(this, items, client);
    }
  }
  public cancelContextMenu() {
    this._cancelContextMenu();
    this._cancelContextMenu = noop;
  }
  // #endregion

  // #region tooltip handling
  public get doTooltip() {
    return this._onTooltip;
  }
  public set doTooltip(value: OnTooltip) {
    this.propertyChanged(
      'onTooltip',
      this._onTooltip,
      (this._onTooltip = value)
    );
  }
  private _onTooltip: OnTooltip;

  public pendTooltip() {
    this.hideTooltip();
    this.hideTooltip = noop;
    this._pendTooltip.cancel();

    if (this.hovered.size > 0) {
      this._pendTooltip();
    }
  }
  public readonly _pendTooltip = debounce(() => {
    if (this.doTooltip && this.hovered.size > 0) {
      this.hideTooltip = this.doTooltip(
        this,
        this.hovered,
        this.pointerViewport
      );
    }
  }, AQBLOCKS_TOOLTIP_DELAY);
  private hideTooltip = noop;

  public cancelTooltip() {
    this.hideTooltip();
    this.hideTooltip = noop;
    this._pendTooltip.cancel();

    return;
  }
  // #endregion

  // #region hovered handling
  public get hovered() {
    return this._hovered;
  }
  private _hovered: ItemSet = createItemSet(BlocksVisualState.Hover);
  public updateHovered = throttle(() => {
    this._updateHovered();
  }, AQBLOCKS_HOVER_DELAY);
  private _updateHovered = () => {
    const { pointer, graph, projection } = this;

    const hovered = createItemSet(0);

    const doit = (node: BlocksNode) => {
      if (rectContains(node.bounds, pointer)) {
        if (node.children && isNodeOpen(node)) {
          for (const child of node.children) {
            if (doit(child)) {
              return true;
            }
          }
        }

        if (isNodeHitVisible(node) && node.parent) {
          hovered.add(node);
          return true;
        }
      }

      return false;
    };

    if (graph && isFinite(pointer[0]) && isFinite(pointer[1])) {
      for (const reference of graph.edges) {
        if (hitTestReference(reference, pointer, projection.scale)) {
          hovered.add(reference);
        }
      }

      if (hovered.size === 0) {
        /* references have priority over nodes */
        doit(graph.root);
      }
    }

    const isChanged =
      hovered.size !== this.hovered.size ||
      !hovered.isSubsetOf(this.hovered) ||
      !this.hovered.isSubsetOf(hovered);

    if (isChanged) {
      /* hovered set changed */
      /* save on component */

      this.hovered.clear();

      for (const item of hovered.values()) {
        this.hovered.add(item);
      }

      /* generate external notification */

      if (graph) {
        this.dispatchEvent(
          new BlocksHoveredEvent({ graph: graph, items: this.hovered })
        );
      }

      /* look after tooltips */

      if (this.hovered.size > 0) {
        this.pendTooltip();
      } else {
        this.cancelTooltip();
      }

      this.pendRender();
    }
  };
  // #endregion

  // #region selected handling
  public get selected() {
    return this._selected;
  }
  public set selected(value: ItemSet) {
    const isChanged =
      value.size !== this.selected.size ||
      !value.isSubsetOf(this.selected) ||
      !this.selected.isSubsetOf(value);

    if (isChanged) {
      /* save on component */
      this._selected.clear();

      for (const item of value.values()) {
        this._selected.add(item);
      }

      if (this.graph) {
        this.dispatchEvent(
          new BlocksSelectedEvent({ graph: this.graph, items: this.selected })
        );
      }
    }
  }
  private _selected: ItemSet = createItemSet(BlocksVisualState.Select);
  // #endregion

  // #region highlighted handling
  public get highlighted() {
    return this._highlighted;
  }
  public set highlighted(value: ItemSet) {
    const isChanged =
      value.size !== this.highlighted.size ||
      !value.isSubsetOf(this.highlighted) ||
      !this.highlighted.isSubsetOf(value);

    if (isChanged) {
      this._highlighted.clear();

      for (const item of value.values()) {
        this._highlighted.add(item);
      }
    }
  }
  private _highlighted: ItemSet = createItemSet(BlocksVisualState.Highlight);
  // #endregion

  // #region canvas
  public get canvas() {
    return this._canvas;
  }
  private set canvas(value: HTMLCanvasElement | null) {
    this.propertyChanged('canvas', this._canvas, (this._canvas = value));
  }
  private _canvas: HTMLCanvasElement | null = null;
  // #endregion

  // #region projection and rendering
  public autoPan = throttle(() => {
    if (this.canvas && isFinite(this.pointerCapture)) {
      const projection = this.projection;
      const window = projection.window;
      const viewport = rectBloat(projection.viewport, -20); // margin to allow autopan even if the viewport is slammed up to the edge of the screen

      if (!rectContains(viewport, this.pointerViewport)) {
        const pointer = this.pointerViewport;

        const speed = [
          (0.1 * rectWidth(window)) / rectWidth(viewport),
          (0.1 * rectHeight(window)) / rectHeight(viewport),
        ];

        const offset = [
          pointer[0] < viewport[0]
            ? pointer[0] - viewport[0]
            : pointer[0] > viewport[2]
              ? pointer[0] - viewport[2]
              : 0,
          pointer[1] < viewport[1]
            ? pointer[1] - viewport[1]
            : pointer[1] > viewport[3]
              ? pointer[1] - viewport[3]
              : 0,
        ];

        const pan = [speed[0] * offset[0], speed[1] * offset[1]];

        this.updateHovered.cancel();
        this.projection.adjustToBounds(
          [
            window[0] + pan[0],
            window[1] + pan[1],
            window[2] + pan[0],
            window[3] + pan[1],
          ],
          0
        );
        this.pointer = this.projection.unprojectPoint(this.pointerViewport);
        // this.updateHovered(); not whilst panning
        this.pendRender();

        this.autoPan(); // schedule another autopan
      }
    }
  }, 30);

  public get projection() {
    return this._projection;
  }
  private _projection = new Projection();

  private readonly resizeObserver = new ResizeObserver(() =>
    this.resizeObserved()
  );

  private resizeObserved() {
    if (!this.canvas) {
      return;
    }

    const { canvas } = this;

    const viewport: Bounds = [
      canvas.offsetLeft,
      canvas.offsetTop,
      canvas.offsetLeft + canvas.offsetWidth,
      canvas.offsetTop + canvas.offsetHeight,
    ];

    if (!(rectWidth(viewport) > 0 && rectHeight(viewport) > 0)) {
      return;
    }

    /* cancel any current operations on the canvas */

    this.updateHovered.cancel();

    /* adjust the projection and recalculate the pointer */

    this.projection.adjustToViewport(viewport, 1);
    this.pointer = this.projection.unprojectPoint(this.pointerViewport);

    /* pend a render so that everything gets drawn */

    this.updateHovered();
    this.pendRender();
  }

  /** the world rectangle an export covers: the whole diagram, snapped outwards. */
  private exportBounds(): Bounds | undefined {
    if (!this.graph) {
      return undefined;
    }

    const bounds = this.graph.root.bounds;

    return [
      Math.floor(bounds[0]),
      Math.floor(bounds[1]),
      Math.ceil(bounds[2]),
      Math.ceil(bounds[3]),
    ];
  }

  /** the world size of the whole diagram, or undefined when there is nothing to export. */
  public getExportContentSize(): Vector | undefined {
    const bounds = this.exportBounds();

    if (!bounds) {
      return undefined;
    }

    return [rectWidth(bounds), rectHeight(bounds)];
  }

  /**
   * The pixel size of a full-diagram export, or undefined when there is nothing
   * to export.
   */
  public getExportSize({
    scale,
    margin,
  }: ExportRenderOptions): Vector | undefined {
    const contentSize = this.getExportContentSize();

    if (!contentSize) {
      return undefined;
    }

    // Floor rather than ceil: a caller that clamped `scale` against a canvas
    // limit computed the bound in exact arithmetic, and rounding the scaled
    // content up can push the total one pixel past it. The projection refits the
    // content to whatever viewport it is given, so the lost sub-pixel costs a
    // fractionally smaller scale rather than cropping anything.
    return [
      Math.floor(contentSize[0] * scale) + 2 * margin,
      Math.floor(contentSize[1] * scale) + 2 * margin,
    ];
  }

  /**
   * Renders the whole diagram into `ctx`, independent of the on-screen viewport,
   * pan and zoom. Interaction affordances — the grid, scrollbars, focus ring and
   * drag preview — are left out, and the lowlight state is ignored so an export
   * taken while a trace is active still shows the whole diagram.
   *
   * @returns false while item animations are still running, and true once the
   * frame is final — including when there was nothing to draw, which callers have
   * to rule out themselves rather than waiting for.
   */
  public renderExport(
    ctx: CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D,
    options: ExportRenderOptions
  ): boolean {
    const bounds = this.exportBounds();
    const size = this.getExportSize(options);

    if (!this.graph || !bounds || !size) {
      return true;
    }

    const projection = new Projection();
    projection.adjustToViewport([0, 0, size[0], size[1]], 1);
    projection.adjustToBounds(bounds, options.margin);

    return renderSceneGraph(this.graph, {
      ctx,
      projection,
      isLowlight: false,
      isReadonly: true,
      prefersReducedMotion: true,
    });
  }

  public pendRender() {
    if (this._animationFrame !== undefined) {
      return;
    }

    this._animationFrame = window.requestAnimationFrame(() => {
      this._animationFrame = undefined;

      const canvas = this.canvas;
      const projection = this.projection;

      /* TODO animate the projection */

      if (canvas && !this.renderFrame(canvas, projection)) {
        this.pendRender();
      }
    });
  }
  private cancelRender() {
    if (this._animationFrame !== undefined) {
      window.cancelAnimationFrame(this._animationFrame);
      this._animationFrame = undefined;
    }
  }
  private _animationFrame: number | undefined;

  private renderFrame(canvas: HTMLCanvasElement, projection: Projection) {
    let done = true;

    if (!this.graph || !this.canvas) {
      return done;
    }

    const viewport: Bounds = [
      canvas.offsetLeft,
      canvas.offsetTop,
      canvas.offsetLeft + canvas.offsetWidth,
      canvas.offsetTop + canvas.offsetHeight,
    ];

    if (!(rectWidth(viewport) > 0 && rectHeight(viewport) > 0)) {
      return done;
    }

    projection.adjustToViewport(viewport, 1 /*window.devicePixelRatio*/);

    const { offsetWidth, offsetHeight } = canvas;

    if (!this.dragHandler) {
      if (canvas.width !== offsetWidth) canvas.width = offsetWidth;
      if (canvas.height !== offsetHeight) canvas.height = offsetHeight;
    }

    const ctx = canvas.getContext('2d', { alpha: false });

    if (!ctx) {
      return done;
    }

    const params = {
      ctx: ctx,
      projection: projection,
      isLowlight: this.highlighted.size !== 0,
      isReadonly: this.isReadonly,
      prefersReducedMotion: this.prefersReducedMotion,
    };

    /* clear canvas */

    params.ctx.globalAlpha = 1;
    params.ctx.resetTransform();
    params.ctx.fillStyle = AQBLOCKS_BACKGROUNDFILL;
    params.ctx.fillRect(
      viewport[0],
      viewport[1],
      rectWidth(viewport),
      rectHeight(viewport)
    );

    if (this.graph && !params.isReadonly) {
      renderGrid(this.graph, params);
    }

    if (this.graph) {
      done = renderSceneGraph(this.graph, params) && done;

      renderScrollbars(this.graph.root.bounds, params);
    }

    if (this.graph && !params.isReadonly) {
      renderDebugGrid(this.graph, params);
    }

    if (this.dragHandler) {
      this.dragHandler.render(params);
    }

    if (this.isFocused) {
      const sz = 2;
      const rc = rectBloat(projection.viewport, -sz / 2);

      params.ctx.beginPath();
      params.ctx.roundRect(rc[0], rc[1], rectWidth(rc), rectHeight(rc), 4);
      params.ctx.strokeStyle = AQBLOCKS_FOCUS_STROKE;
      params.ctx.lineWidth = sz;
      params.ctx.stroke();
    }

    return done; // if any animations are still running, return false (not done rendering)
  }
  // #endregion

  // #region pointer state
  public pointerViewport: Vector = [NaN, NaN];
  public pointerDownViewport: Vector = [NaN, NaN];
  public pointer: Vector = [NaN, NaN];
  public pointerDown: Vector = [NaN, NaN];
  public dragHandler: DragHandler | null = null;
  public get pointerCapture() {
    return this._pointerCapture;
  }
  private set pointerCapture(value: number) {
    this.propertyChanged(
      'pointerCapture',
      this._pointerCapture,
      (this._pointerCapture = value)
    );
  }
  private _pointerCapture: number = NaN;

  public get isFocused() {
    return this._isFocused;
  }
  private set isFocused(value: boolean) {
    this.propertyChanged(
      'isFocused',
      this._isFocused,
      (this._isFocused = value)
    );
  }
  private _isFocused = false;
  // #endregion

  public cancel() {
    this.cancelTooltip();
    this.cancelContextMenu();

    if (this.dragHandler) {
      this.dragHandler.cancel();
      this.dragHandler = null;
    }

    if (this.canvas && isFinite(this.pointerCapture)) {
      this.canvas.releasePointerCapture(this.pointerCapture);
    }
  }

  protected propertyChanged(
    name: ObservedPropertyName,
    oldValue: unknown,
    value: unknown
  ) {
    switch (name) {
      case 'graph':
        if (oldValue !== value) {
          const oldGraph = oldValue as BlocksGraph | null;

          if (this.graph) {
            const update = mergeGraph(this, this.graph, oldGraph);

            fixupLayout(this.graph, this.layoutStyle);
            placeAndRoute(
              this.graph,
              this.layoutStyle.linksAggregation,
              this.placementStyle
            ); // depending on what happened in fixup

            const nodes_total = update.nodesAdded + update.nodesReused;

            if (update.nodesReused < 0.5 * nodes_total) {
              this.zoomFit();
            }
          }

          this.pendRender();
        }

        break;

      case 'layoutStyle':
        if (this.graph && !isEqual(oldValue, value)) {
          const { type, orientation, linksAggregation } = this.layoutStyle;
          const oldLayoutStyle = oldValue as BlocksLayoutStyle;

          if (
            oldLayoutStyle.type !== type ||
            oldLayoutStyle.orientation !== orientation
          ) {
            resetLayout(this.graph, this.layoutStyle);
            fixupLayout(this.graph, this.layoutStyle);
            placeAndRoute(this.graph, linksAggregation, this.placementStyle);

            this.pendRender();
          } else if (oldLayoutStyle.linksAggregation !== linksAggregation) {
            placeAndRoute(
              this.graph,
              this.layoutStyle.linksAggregation,
              this.placementStyle
            );

            this.pendRender();
          }
        }

        break;

      case 'placementStyle':
        if (this.graph && !isEqual(oldValue, value)) {
          placeAndRoute(
            this.graph,
            this.layoutStyle.linksAggregation,
            this.placementStyle
          );
          this.pendRender();
        }

        break;

      case 'onContextMenu':
        if (oldValue !== value) {
          this.cancelContextMenu();
        }

        break;

      case 'onTooltip':
        if (oldValue !== value) {
          this.cancelTooltip();
        }

        break;

      case 'isReadonly':
        if (oldValue !== value) {
          this.cancel(); // cancel any pending edits and whatnot
          this.pendRender();
        }

        break;

      case 'isDisabled':
        if (oldValue !== this.isDisabled) {
          this.cancel();

          if (this.canvas) {
            this.canvas.style.pointerEvents = this.isDisabled ? 'none' : 'auto';
          }

          this.pendRender();
        }
        break;

      case 'isFocused':
      case 'isUserSelectable':
      case 'prefersReducedMotion':
        if (oldValue !== value) {
          this.pendRender();
        }
        break;

      /* internal properties */

      case 'canvas':
        if (oldValue !== value && oldValue) {
          const oldCanvas = oldValue as HTMLCanvasElement;

          /* detach the interaction event handlers */

          detachCanvasEvents(this, oldCanvas);

          /* detach the internal event handlers */

          oldCanvas.onblur = null;
          oldCanvas.onfocus = null;
          oldCanvas.ongotpointercapture = null;
          oldCanvas.onlostpointercapture = null;

          this.cancelRender();
          this.cancel(); // cancel any pending edits and whatnot
        }

        if (oldValue !== value && this.canvas) {
          /* attach the interaction event handlers */

          attachCanvasEvents(this, this.canvas);

          /* attach the internal event handlers */

          this.canvas.onblur = () => (this.isFocused = false);
          this.canvas.onfocus = () => (this.isFocused = true);
          this.canvas.ongotpointercapture = (e: PointerEvent) =>
            (this.pointerCapture = e.pointerId);
          this.canvas.onlostpointercapture = () => (this.pointerCapture = NaN);

          this.canvas.style.pointerEvents = this.isDisabled ? 'none' : 'auto';

          const viewport: Bounds = [
            this.canvas.offsetLeft,
            this.canvas.offsetTop,
            this.canvas.offsetLeft + this.canvas.offsetWidth,
            this.canvas.offsetTop + this.canvas.offsetHeight,
          ];
          this.projection.adjustToViewport(
            viewport,
            1 /*window.devicePixelRatio*/
          );

          if (this.graph) {
            this.zoomFit();
            this.pendRender();
          }
        }

        break;

      case 'pointerCapture':
        if (oldValue !== value) {
          const oldPointerCapture = oldValue as number;

          if (!isFinite(oldPointerCapture) && isFinite(this.pointerCapture)) {
            this.pendRender();
          }

          if (isFinite(oldPointerCapture) && !isFinite(this.pointerCapture)) {
            this.dragHandler = null;
            this.pendRender();
          }
        }

        break;
    }
  }

  // #region webcomponent
  public connectedCallback() {
    const shadow = this.attachShadow({ mode: 'closed', delegatesFocus: true });

    shadow.innerHTML = `
      <style>
        canvas { position: absolute; inset: 0; display: block; width: 100%; height: 100%; outline: 0; touch-action: none }
      </style>
      <canvas tabindex="0"></canvas>`;

    this.canvas = shadow.querySelector('canvas');

    if (this.canvas) {
      this.resizeObserver.observe(this.canvas);
      this._isFocused = shadow.activeElement === this.canvas;
    }
  }

  public disconnectedCallback() {
    this._isFocused = false;
    this.resizeObserver.disconnect();
    this.canvas = null;
  }
  // #endregion
}

/* TODO All of the following is some sort of global type registry thing and really shouldn't be in this file. except perhaps AdqBlocksProps */

/* register as a component */
window.customElements.define('aq-blocks', AqBlocksComponent);

type AdqBlocksProps = {
  /* props for react tooling */
  doContextMenu?: OnContextMenu;
  doTooltip?: OnTooltip;
  onActivated?: (event: BlocksActivatedEvent) => void;
  onHovered?: (event: BlocksHoveredEvent) => void;
  onSelected?: (event: BlocksSelectedEvent) => void;
  onEdited?: (event: BlocksEditedEvent) => void;
  onFindRequested?: (event: BlocksFindRequestedEvent) => void;

  isReadonly?: boolean;
  isDisabled?: boolean;
  isUserSelectable?: boolean;
  prefersReducedMotion?: boolean;
  graph?: BlocksGraph | null;
  placementStyle?: BlocksPlacementStyle;
  layoutStyle?: BlocksLayoutStyle;
  // hovered: ItemSet;  // not an attribute because must be read-only
  // highlighted: ItemSet;  // not an attribute because must be read-only
  // selected: ItemSet;  // not an attribute because must be read-only
};

declare module 'react/jsx-runtime' {
  /* add web component to global react intrinsic namespace */
  namespace JSX {
    interface IntrinsicElements {
      'aq-blocks': React.DetailedHTMLProps<
        React.HTMLAttributes<HTMLElement>,
        HTMLElement
      > &
        AdqBlocksProps;
    }
  }
}
